import React, { useState } from "react";
import {
    Briefcase,
    MapPin,
    Calendar,
    ChevronRight,
    Plus,
    Pencil,
    Trash2,
    RotateCcw,
    Users,
    Loader2,
} from "lucide-react";
import DocCountBadge from "./DocCountBadge";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";

function formatDate(iso) {
    if (!iso) return "";
    // Accept full ISO timestamps (e.g. "2026-04-23T09:49:36.665813+00:00")
    // or bare YYYY-MM-DD. Extract the date portion before formatting.
    const datePart = String(iso).split("T")[0];
    const [y, m, d] = datePart.split("-");
    if (!y || !m || !d) return datePart;
    return `${m}/${d}/${y}`;
}

export const RecentProjects = ({
    projects = [],
    loading = false,
    total,
    uploadCounts = {},
    onViewAll,
    onSelect,
    onNew,
    onEdit,
    onReset,
    onDelete,
    onShareToWorkgroup,
}) => {
    const totalCount = typeof total === "number" ? total : projects.length;
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const confirmDelete = async () => {
        if (!pendingDelete || !onDelete) return;
        setDeleting(true);
        try {
            await onDelete(pendingDelete);
            setPendingDelete(null);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <section
            className="bg-[#131A2F]/80 border border-[#2A3655] rounded-md overflow-hidden flex-1 min-h-0 flex flex-col"
            data-testid="recent-projects-section"
        >
            <header className="flex items-center justify-between px-5 py-3 border-b border-[#2A3655] bg-[#1C253C] shrink-0">
                <h2 className="text-base font-bold text-white tracking-tight flex items-center gap-2">
                    <span className="w-1.5 h-5 bg-[#00A3FF] rounded-sm" />
                    Recent Projects
                </h2>
                <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-bold">
                        {totalCount} active
                    </span>
                    {onNew && (
                        <button
                            type="button"
                            onClick={onNew}
                            aria-label="New project"
                            data-testid="btn-new-project-inline"
                            className="w-7 h-7 rounded-sm bg-[#007AFF] hover:bg-[#3395FF] text-white flex items-center justify-center shadow-[0_0_14px_rgba(0,122,255,0.35)] active:scale-95 transition"
                        >
                            <Plus className="w-4 h-4" strokeWidth={3} />
                        </button>
                    )}
                </div>
            </header>
            {/* The list owns the vertical scroll — `flex-1 min-h-0` + 
                `overflow-y-auto` lets the parent section bound the
                height and the list scroll internally when projects
                overflow. Rows themselves are content-sized (no flex-1
                per-li, which was making 4+ rows compress and visually
                overlap). */}
            <ul className="divide-y divide-[#2A3655] flex-1 min-h-0 overflow-y-auto" data-testid="recent-projects-list">
                {loading && (
                    <li className="p-5 text-sm text-slate-400">
                        Loading projects...
                    </li>
                )}
                {!loading && projects.length === 0 && (
                    <li
                        className="p-5 text-sm text-slate-400"
                        data-testid="projects-empty"
                    >
                        No projects yet. Click "+ New Project" to get started.
                    </li>
                )}
                {projects.map((p, i) => (
                    <li
                        key={p.id}
                        className="group px-4 md:px-5 py-3 md:py-4 hover:bg-[#1C253C] transition-colors cursor-pointer anim-rise"
                        style={{ animationDelay: `${i * 60}ms` }}
                        data-testid={`project-item-${p.id}`}
                        onClick={() => onSelect && onSelect(p)}
                    >
                        {/* iter-52: flex-col on phones so the action
                            icon strip wraps onto its own row, and
                            row-layout on sm+ like before. */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full min-w-0">
                            <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                                <div className="w-10 h-10 rounded-sm bg-[#1C253C] border border-[#2A3655] text-[#00A3FF] flex items-center justify-center shrink-0 group-hover:border-[#00A3FF] transition-colors">
                                    <Briefcase className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                        <div className="font-semibold text-white truncate">
                                            {p.name}
                                        </div>
                                        {p.job_id && (
                                            <span
                                                className="shrink-0 font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#007AFF]/15 border border-[#007AFF]/40 text-[#00A3FF]"
                                                data-testid={`project-job-id-${p.id}`}
                                            >
                                                {p.job_id}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-slate-400">
                                        <span className="flex items-center gap-1 min-w-0">
                                            <MapPin className="w-3 h-3 shrink-0" />
                                            <span className="truncate">{p.location}</span>
                                        </span>
                                        <span className="flex items-center gap-1 shrink-0">
                                            <Calendar className="w-3 h-3" />
                                            <span className="sm:hidden">{formatDate(p.updated_at)}</span>
                                            <span className="hidden sm:inline">Updated {formatDate(p.updated_at)}</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {/* Action cluster — always right-aligned; wraps
                                to its own row below the content on mobile. */}
                            <div
                                className="flex items-center gap-1 justify-end sm:shrink-0 sm:ml-auto"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <DocCountBadge
                                    count={uploadCounts[p.id] || 0}
                                    testId={`project-doc-count-${p.id}`}
                                />
                                {onEdit && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEdit(p);
                                        }}
                                        data-testid={`btn-edit-project-${p.id}`}
                                        aria-label={`Edit ${p.name}`}
                                        className="shrink-0 w-9 h-9 rounded-sm flex items-center justify-center text-slate-400 hover:text-[#00A3FF] hover:bg-[#007AFF]/10 border border-transparent hover:border-[#00A3FF]/30 transition-colors active:scale-95"
                                    >
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                )}
                                {onShareToWorkgroup && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onShareToWorkgroup(p);
                                        }}
                                        data-testid={`btn-share-wg-project-${p.id}`}
                                        aria-label={`Share ${p.name} to workgroup`}
                                        title="Share to workgroup"
                                        className="shrink-0 w-9 h-9 rounded-sm flex items-center justify-center text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 border border-transparent hover:border-emerald-500/30 transition-colors active:scale-95"
                                    >
                                        <Users className="w-4 h-4" />
                                    </button>
                                )}
                                {onReset && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onReset(p);
                                        }}
                                        data-testid={`btn-reset-project-${p.id}`}
                                        aria-label={`Reset ${p.name}`}
                                        title="Reset takeoffs"
                                        className="shrink-0 w-9 h-9 rounded-sm flex items-center justify-center text-slate-400 hover:text-amber-400 hover:bg-amber-500/10 border border-transparent hover:border-amber-500/30 transition-colors active:scale-95"
                                    >
                                        <RotateCcw className="w-4 h-4" />
                                    </button>
                                )}
                                {onDelete && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setPendingDelete(p);
                                        }}
                                        data-testid={`btn-delete-project-${p.id}`}
                                        aria-label={`Delete ${p.name}`}
                                        className="shrink-0 w-9 h-9 rounded-sm flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/30 transition-colors active:scale-95"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                                <ChevronRight className="hidden sm:block w-4 h-4 text-slate-500 group-hover:text-[#00A3FF] transition-colors shrink-0" />
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
            {totalCount > projects.length && onViewAll && (
                <button
                    type="button"
                    onClick={onViewAll}
                    data-testid="btn-view-all-projects"
                    className="shrink-0 w-full px-5 py-2.5 text-sm font-medium text-[#00A3FF] hover:text-white hover:bg-[#1C253C] border-t border-[#2A3655] transition-colors flex items-center justify-center gap-1.5"
                >
                    View all {totalCount} projects
                    <ChevronRight className="w-4 h-4" />
                </button>
            )}

            <AlertDialog
                open={!!pendingDelete}
                onOpenChange={(o) => !o && !deleting && setPendingDelete(null)}
            >
                <AlertDialogContent
                    className="bg-[#131A2F] border border-[#2A3655] text-white w-[calc(100vw-1.5rem)] max-w-[440px]"
                    data-testid="recent-delete-project-confirm"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">
                            Delete this project?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                            <span className="text-white font-semibold">
                                {pendingDelete?.name}
                            </span>{" "}
                            will be permanently removed from your workspace.
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid="btn-cancel-delete-recent-project"
                            className="bg-[#1C253C] border-[#2A3655] text-slate-300 hover:bg-[#2A3655] hover:text-white"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            disabled={deleting}
                            data-testid="btn-confirm-delete-recent-project"
                            className="bg-red-600 hover:bg-red-500 text-white font-bold"
                        >
                            {deleting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                </>
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </section>
    );
};

export default RecentProjects;
